<template>
  <section
    id="otzivi"
    class="lpu-reviews d-flex flex-column"
    :class="isMobile ? 'ga-4' : 'ga-6'"
    data-qa="lpu_reviews"
  >
    <template v-if="hasReviews">
      <div
        itemprop="aggregateRating"
        itemscope
        itemtype="https://schema.org/AggregateRating"
      >
        <meta
          itemprop="itemReviewed"
          :content="reviewedName"
        >
        <meta
          itemprop="ratingValue"
          :content="ratingValue"
        >
        <meta
          itemprop="worstRating"
          content="0"
        >
        <meta
          itemprop="bestRating"
          content="100"
        >
        <meta
          itemprop="ratingCount"
          :content="String(lpu.allrates)"
        >
      </div>

      <div
        :class="isMobile
          ? 'd-flex flex-column ga-4'
          : 'd-flex align-center justify-space-between'"
      >
        <div
          class="d-flex"
          :class="isMobile ? 'flex-column ga-5' : 'align-center ga-5'"
        >
          <div
            class="d-flex align-center"
            :class="{ 'justify-space-between w-100': isMobile }"
          >
            <h2
              data-qa="lpu_reviews_title"
              class="lpu-section-title text--text"
              :class="isMobile ? 'text-h6' : 'text-h5'"
              :data-count="String(lpu.allrates)"
            >
              {{ tStatic('Отзывы') }}
            </h2>

            <a
              v-if="showAllReviewsLink"
              :href="allReviewsUrl"
              class="text-body-2 primary--text text-decoration-underline"
              data-qa="lpu_reviews_all"
              data-reviews-list-yandex-goals="reviews_list_click_show_all_reviews"
            >
              {{ tStatic('Все отзывы') }}
            </a>
          </div>

          <CorePRG
            :url="helpUrl"
            :button-attrs="helpButtonAttrs"
          >
            <span class="text-body-1 primary--text text-decoration-underline">
              {{ tStatic('Как мы проверяем отзывы?') }}
            </span>
            <span
              v-if="isMobile"
              class="ui-icon-link-2 ui-icon-fz-smaller primary--text ml-1"
            />
          </CorePRG>
        </div>

        <div
          v-if="showPrsPlate || showLeaveReviewButton"
          class="d-flex"
          :class="isMobile
            ? 'flex-column ga-4'
            : 'align-center ga-4'"
        >
          <ReviewsPrsPlate
            v-if="showPrsPlate"
            :url="prsLandingUrl"
          />
          <ReviewsLeaveBtn
            v-if="showLeaveReviewButton"
            :lpu-id="lpu.id"
          />
        </div>
      </div>

      <ReviewsFraudAlert v-if="hasFraudMark" />
      <ReviewsTags
        v-if="showTags"
        :tags="tags"
        :redirect-pathname="ratesPath"
      />
      <ReviewsList
        :rates="rates"
        :lpu="lpu"
        :is-expanded="isExpanded"
        @expand="isExpanded = true"
      />
      <ReviewsBottom
        :allrates="lpu.allrates"
        :rates-count="rates.length"
        :lpu-id="lpu.id"
        :rates-path="ratesPath"
        :can-leave-review="canLeaveReviewAllowed"
        :is-expanded="isExpanded"
        @expand="isExpanded = true"
      />
    </template>

    <template v-else>
      <h2
        data-qa="lpu_reviews_title"
        class="text--text"
        :class="isMobile ? 'text-h6' : 'text-h5'"
      >
        {{ tStatic('Отзывы') }}
      </h2>
      <ReviewsEmpty
        :lpu-id="lpu.id"
        :lpu-name="reviewedName"
        :show-leave-button="showEmptyLeaveReviewButton"
      />
    </template>
  </section>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import filterStarsPercent from 'common/filters/filterStarsPercent'
import useIsMobile from 'src/composables/useIsMobile'
import CorePRG from 'src/core/CorePRG/CorePRG.vue'
import type { ContextStore } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/index/types'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import useContextStore from 'src/store/useContextStore'
import useURLStore from 'src/store/useURLStore'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import canLeaveReview from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/canLeaveReview'
import ReviewsBottom from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/ReviewsBottom.vue'
import ReviewsEmpty from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/ReviewsEmpty.vue'
import ReviewsFraudAlert from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/ReviewsFraudAlert.vue'
import ReviewsLeaveBtn from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/ReviewsLeaveBtn.vue'
import ReviewsList from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/ReviewsList.vue'
import ReviewsPrsPlate from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/ReviewsPrsPlate.vue'
import ReviewsTags from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/ReviewsTags.vue'
import getPageRates from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/functions/getPageRates'
import getReviewsHelpUrl from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/functions/getReviewsHelpUrl'
import mapReviewsTags from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/functions/mapReviewsTags'

const props = defineProps<{
  lpu: Lpu
}>()

const isMobile = useIsMobile()
const { context, urlLang }: ContextStore = useContextStore()
const { reverseFullL10N } = useURLStore()
const isExpanded = ref(false)

const reviewedName = computed(() => tDynamic(props.lpu, 'name'))
const hasReviews = computed(() => props.lpu.allrates > 0)
const ratingValue = computed(() => filterStarsPercent(props.lpu.stars ?? 0))
const hasFraudMark = computed(() => Boolean(props.lpu.has_fraud_mark))
const tags = computed(() => mapReviewsTags(context.summarization_tags ?? []))
const showTags = computed(() => hasReviews.value && props.lpu.allrates > 10 && tags.value.length > 0)
const rates = computed(() => getPageRates(context.rates, context.ratelpus))

const ratesPath = reverseFullL10N({
  name: 'lpu_rates',
  args: {
    language: urlLang,
    town_translit: props.lpu.town.translit,
    lpu_translit: props.lpu.translit,
  },
})

const helpUrl = computed(() => getReviewsHelpUrl(
  context.request.country.iso,
  Boolean(context.request.country.is_russia),
))

const helpButtonAttrs = computed(() => ({
  class: 'lpu-reviews__help-btn d-inline-flex align-center',
  'data-qa': 'lpu_reviews_help',
}))

const showPrsPlate = computed(() => (
  Boolean(props.lpu.is_prs_lpu)
  && !props.lpu.closed
))

const prsLandingUrl = computed(() => context.prs_landing || '')

const canLeaveReviewAllowed = computed(() => canLeaveReview(context, props.lpu.closed))

const showLeaveReviewButton = computed(() => canLeaveReviewAllowed.value && hasReviews.value)
const showEmptyLeaveReviewButton = computed(() => canLeaveReviewAllowed.value && !hasReviews.value)

const showAllReviewsLink = computed(() => (
  isMobile.value
  && !props.lpu.is_not_index_by_rates_count
  && props.lpu.allrates > 10
))

const allReviewsUrl = computed(() => `${ratesPath}#h1_title`)
</script>

<style scoped lang="scss">
.lpu-reviews {
  ::v-deep .lpu-reviews__help-btn {
    border: 0;
    background: transparent;
    cursor: pointer;
    padding: 0;
    font: inherit;
  }
}
</style>
